import { useState } from "react";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../ToxnotReactModal";
import DataTable from "../DataTable";
import { DeclarePackagingLayers, PackagingLayerDescription, PackagingLayerType } from "../../../utilities/PackagingLayer/DeclarePackagingLayers";
import { DropdownMenu } from "../DropdownMenu";
import TextInput from "../TextInput";

import './styles.css';

interface PackagingDataTableProps {
    columns: string[];
    rows: string[][];
    localizer: IStringLocalizer;
    onResponseChange: (value: any) => void;
    isDisabled?: boolean;
}

const PackagingDataTable: React.FC<PackagingDataTableProps> = ({ columns, rows, localizer, onResponseChange }) => {
    const modalId = "packaging-modal";
    const validationMessage = "Answer is required";
    const emptyLayerType: PackagingLayerType = { layerDescriptions: [], layerName: '', layerType: '' };
    const emptyLayerDescription: PackagingLayerDescription = { id: -1, descriptionName: '', descriptionType: '' };
    const packagingLayers = DeclarePackagingLayers.getAllPackagingLayers();
    const [rowsValue, setRowsValue] = useState(rows || []);
    const [currentRowIndexValue, setCurrentRowIndexValue] = useState(-1);
    const [layerTypeValue, setLayerTypeValue] = useState<PackagingLayerType>(emptyLayerType);
    const [layerDescriptionValue, setLayerDescriptionValue] = useState<PackagingLayerDescription>(emptyLayerDescription);
    const [productNameValue, setProductNameValue] = useState("");
    const [manufacturerValue, setManufacturerValue] = useState("");
    const [thicknessValue, setThicknessValue] = useState("");
    const [submittedStateValue, setSubmittedStateValue] = useState(false);

    const openModal = () => {
        $(`#${modalId}`).modal("show");
    }

    const closeModal = () => {
        $(`#${modalId}`).modal("hide");
    }


    const addLayer = () => {
        setCurrentRowIndexValue(rows.length);

        setProductNameValue('');
        setManufacturerValue('');
        setThicknessValue('');
        setLayerTypeValue(emptyLayerType);
        setLayerDescriptionValue(emptyLayerDescription);

        openModal();
    }

    const editRow = (rowIndex: number) => {
        setCurrentRowIndexValue(rowIndex);

        const row = rows[rowIndex];
        const layer = packagingLayers.find(l => l.layerName === row[0]);

        if (layer) {
            setLayerTypeValue(layer);

            const layerDescription = layer.layerDescriptions.find(d => d.descriptionName === row[1]);

            if (layerDescription) {
                setLayerDescriptionValue(layerDescription);
            }
        }

        setProductNameValue(row[2]);
        setManufacturerValue(row[3]);
        setThicknessValue(row[4]);

        openModal();
    }

    const onChange = (value: any) => {
        setRowsValue(value);
        onResponseChange(value);
    }

    const submit = () => {
        setSubmittedStateValue(true);

        if (productNameValue === '' ||
            layerTypeValue.layerType === '' ||
            (layerTypeValue.layerDescriptions?.length > 0 && layerDescriptionValue?.descriptionType === '')
        ) {
            return;
        }

        const currentEditRow = [layerTypeValue.layerName, layerDescriptionValue.descriptionName, productNameValue, manufacturerValue, thicknessValue];
        const updatedMatrix = [...rowsValue];

        if (updatedMatrix.length === 0) {
            updatedMatrix.push(currentEditRow);
        } else {
            updatedMatrix[currentRowIndexValue] = currentEditRow;
        }

        cleanupState();
        setSubmittedStateValue(false);
        setRowsValue(updatedMatrix)
        onResponseChange(updatedMatrix);

        closeModal();
    }

    const onLayerChange = (newValue: PackagingLayerType) => {
        setLayerTypeValue({ ...newValue });
    }

    const requiredColumns = columns.map((c, index) => {
        if (index === 0 || index === 1) {
            return true;
        }

        return false;
    });

    const getPackagingLayers = (): PackagingLayerType[] => {
        return [emptyLayerType].concat(packagingLayers);
    }

    const getPackagingLayerDescriptions = (): PackagingLayerDescription[] => {
        if (layerTypeValue.layerDescriptions?.length > 0) {
            return [emptyLayerDescription].concat(layerTypeValue.layerDescriptions);
        }

        return [];
    }

    const cleanupState = () => {
        setLayerTypeValue(emptyLayerType);
        setLayerDescriptionValue(emptyLayerDescription);
    }

    return (
        <>
            <div className="row packaging-data-table-wrapper">
                <div className="col-xs-10 col-sm-9 col-lg-10 col-xl-11">
                    <div className="generic-table-parent data-table-parent">
                        <DataTable
                            localizer={localizer}
                            columns={columns}
                            rows={rowsValue}
                            onResponseChange={onChange}
                            radioColumns={[]}
                            inlineEditor={false}
                            addButtonText={localizer.localizeString("Add Layer")}
                            customAdd={addLayer}
                            customEdit={editRow}
                            dragDropEnabled={true}
                            requiredColumns={requiredColumns}
                        />

                        <ToxnotReactModal modalId={modalId} localizedTitle={localizer.localizeString("Add Layer")} onClose={cleanupState} >
                            <ToxnotReactModalBody>
                                <div className="form-group">
                                    <label>
                                        {localizer.localizeString("Layer Type")}
                                        <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span>
                                    </label>
                                    <DropdownMenu options={getPackagingLayers()}
                                        onOptionSelected={onLayerChange}
                                        getFriendlyNameForOptions={t => localizer.localizeString(t.layerName)}
                                        value={layerTypeValue}
                                        getUniqueValueForOption={t => t.layerType}
                                    />
                                    {submittedStateValue && layerTypeValue.layerType === '' &&
                                        <p className="m-t-5 text-danger">{localizer.localizeString(validationMessage)}</p>
                                    }
                                </div>
                                <div className="form-group">
                                    <label>
                                        {localizer.localizeString("Layer Description")}
                                        {layerTypeValue.layerDescriptions?.length > 0 &&
                                            <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span>
                                        }
                                    </label>
                                    <DropdownMenu options={getPackagingLayerDescriptions()}
                                        onOptionSelected={(newValue: PackagingLayerDescription) => setLayerDescriptionValue({ ...newValue })}
                                        getFriendlyNameForOptions={d => localizer.localizeString(d.descriptionName)}
                                        value={layerDescriptionValue}
                                        getUniqueValueForOption={d => d.id.toString()}
                                        isDisabled={layerTypeValue.layerDescriptions.length === 0}
                                    />
                                    {submittedStateValue && layerTypeValue.layerDescriptions?.length > 0 && layerDescriptionValue?.descriptionType === '' &&
                                        <p className="m-t-5 text-danger">{localizer.localizeString(validationMessage)}</p>
                                    }
                                </div>
                                <div className="form-group">
                                    <label>
                                        {localizer.localizeString("Product Name")}
                                        <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span>
                                    </label>
                                    <TextInput value={productNameValue}
                                        onValueChange={(value: string) => setProductNameValue(value)}
                                    />
                                    {submittedStateValue && productNameValue === '' &&
                                        <p className="m-t-5 text-danger">{localizer.localizeString(validationMessage)}</p>
                                    }
                                </div>
                                <div className="form-group">
                                    <label>
                                        {localizer.localizeString("Manufacturer")}
                                        <span className="p-relative m-l-5"></span>
                                    </label>
                                    <TextInput value={manufacturerValue}
                                        onValueChange={(value: string) => setManufacturerValue(value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>
                                        {localizer.localizeString("Thickness/Weight")}
                                        <span className="p-relative m-l-5"></span>
                                    </label>
                                    <TextInput value={thicknessValue}
                                        onValueChange={(value: string) => setThicknessValue(value)}
                                    />
                                </div>
                            </ToxnotReactModalBody>
                            <ToxnotReactModalFooter onCancel={cleanupState} localizer={localizer}>
                                <button type="button" className="btn btn-toxnot-primary" onClick={submit}>{localizer.localizeString("Submit")}</button>
                            </ToxnotReactModalFooter>
                        </ToxnotReactModal>
                    </div>
                </div>
                <div className="col-xs-2 col-sm-3 col-lg-2 col-xl-1 p-relative">
                    <div className="arrow-placeholder">
                        <div className="text-center p-b-5">{localizer.localizeString("Outer Layer")}</div>
                        <div className="text-center">
                            <i className="material-icons">arrow_downward</i>
                        </div>
                        <div className="text-center">{localizer.localizeString("Inner Layer")}</div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default PackagingDataTable;